import { CheckCircle2, Circle, AlertCircle, Clock, XCircle, MapPin } from "lucide-react";

const PETROLE = "#284E55";

const REQUIRED_DOC_TYPES = ["identity", "kbis", "rc_pro"];
const DOC_LABELS = { identity: "Pièce d'identité", kbis: "KBIS", rc_pro: "Attestation RC Professionnelle" };

const STEP_DEFS = [
  { key: "candidature", num: "01", label: "Candidature" },
  { key: "dossier", num: "02", label: "Dossier professionnel" },
  { key: "validation", num: "03", label: "Validation CozyHome" },
  { key: "souscription", num: "04", label: "Souscription" },
  { key: "espace", num: "05", label: "Espace Partenaire" },
  { key: "activation", num: "06", label: "Activation du territoire" },
  { key: "acces", num: "07", label: "Accès complet" },
];

const STATE_META = {
  termine: { label: "Terminé", cls: "border-sage/40 bg-sage/15 text-navy", dot: "bg-sage border-sage", icon: CheckCircle2 },
  en_cours: { label: "En cours", cls: "border-sky-200 bg-sky-50 text-sky-700", dot: "border-sky-500 bg-white", icon: Clock },
  action_requise: { label: "Action requise", cls: "border-amber-200 bg-amber-50 text-amber-700", dot: "border-amber-500 bg-white", icon: AlertCircle },
  a_venir: { label: "À venir", cls: "border-slate-200 bg-slate-50 text-slate-400", dot: "border-slate-300 bg-white", icon: Circle },
  refuse: { label: "Refusé", cls: "border-red-200 bg-red-50 text-red-600", dot: "border-red-500 bg-white", icon: XCircle },
};

const BLOCKED_PARTNER_STATUSES = ["ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE", "ACTIF", "SUSPENDU", "RESILIE"];

export function deriveJourneySteps({ appStatus, documents, partnerStatus }) {
  const docs = documents || {};
  const required = REQUIRED_DOC_TYPES.map((t) => ({ type: t, ...(docs[t] || {}) })).filter((d) => d.blocking !== false);
  const allValid = required.length > 0 && required.every((d) => d.status === "valide");
  const anyRefused = required.some((d) => d.status === "refuse");
  const anySent = required.some((d) => d.status === "envoye");
  const centraleEngaged = appStatus && appStatus !== "NOUVELLE_CANDIDATURE";
  const missingDocs = required.filter((d) => d.status === "a_fournir" || d.status === "refuse");

  let s2 = "a_venir";
  if (allValid) s2 = "termine";
  else if (anyRefused || appStatus === "DOCUMENTS_A_FOURNIR" || partnerStatus === "DOCUMENTS_EN_ATTENTE") s2 = "action_requise";
  else if (anySent) s2 = "en_cours";
  else if (centraleEngaged) s2 = "action_requise";

  let s3 = "a_venir";
  if (appStatus === "VALIDEE") s3 = "termine";
  else if (appStatus === "REFUSEE") s3 = "refuse";
  else if (centraleEngaged) s3 = "en_cours";

  let s4 = "a_venir";
  if (partnerStatus === "VALIDE") s4 = "action_requise";
  else if (["PAIEMENT_EN_ATTENTE", "DOCUMENTS_EN_ATTENTE"].includes(partnerStatus)) s4 = "en_cours";
  else if (BLOCKED_PARTNER_STATUSES.includes(partnerStatus)) s4 = "termine";

  const s5 = BLOCKED_PARTNER_STATUSES.includes(partnerStatus) ? "termine" : "a_venir";

  let s6 = "a_venir";
  if (partnerStatus === "ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE") s6 = "en_cours";
  else if (["ACTIF", "SUSPENDU", "RESILIE"].includes(partnerStatus)) s6 = "termine";

  const s7 = partnerStatus === "ACTIF" ? "termine" : "a_venir";

  return [
    { key: "candidature", state: "termine" },
    { key: "dossier", state: s2, missingDocs },
    { key: "validation", state: s3 },
    { key: "souscription", state: s4 },
    { key: "espace", state: s5 },
    { key: "activation", state: s6 },
    { key: "acces", state: s7 },
  ];
}

export const PartnerJourneyStepper = ({ appStatus, documents, partnerStatus, onGoToDocuments }) => {
  const steps = deriveJourneySteps({ appStatus, documents, partnerStatus });

  return (
    <div data-testid="partner-journey-stepper" className="mt-8 rounded-3xl border-2 border-[#EDEAE3] bg-white p-5 sm:p-7">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Votre parcours Partenaire</p>
      <div className="mt-5 space-y-0">
        {steps.map((step, i) => {
          const def = STEP_DEFS.find((d) => d.key === step.key);
          const meta = STATE_META[step.state];
          const Icon = meta.icon;
          const isLast = i === steps.length - 1;
          return (
            <div key={step.key} data-testid={`journey-step-${step.key}`} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold ${meta.dot}`} style={step.state === "termine" ? { color: "#fff" } : { color: PETROLE }}>
                  {step.state === "termine" ? <CheckCircle2 className="h-4 w-4 text-white" /> : def.num}
                </span>
                {!isLast && <span className={`mt-1 w-0.5 flex-1 ${step.state === "a_venir" ? "bg-slate-200" : "bg-[#284E55]/30"}`} style={{ minHeight: "28px" }} />}
              </div>
              <div className={`flex-1 ${isLast ? "" : "pb-6"}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-navy">{def.label}</p>
                  <span data-testid={`journey-step-badge-${step.key}`} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold ${meta.cls}`}>
                    <Icon className="h-3 w-3" /> {meta.label}
                  </span>
                </div>

                {step.key === "dossier" && (step.state === "action_requise" || step.state === "en_cours") && step.missingDocs.length > 0 && (
                  <div data-testid="journey-dossier-detail" className="mt-3 rounded-2xl bg-amber-50/60 p-4">
                    <p className="text-xs font-semibold text-amber-700">
                      {step.state === "action_requise" ? "Éléments à transmettre :" : "En attente de validation :"}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {step.missingDocs.map((d) => (
                        <li key={d.type} className="flex items-center gap-2 text-xs text-amber-700">
                          <span className="h-1 w-1 rounded-full bg-amber-500" />
                          {DOC_LABELS[d.type]}
                          {d.status === "refuse" && d.rejection_reason ? ` — refusé : ${d.rejection_reason}` : ""}
                        </li>
                      ))}
                    </ul>
                    {step.state === "action_requise" && (
                      <button
                        type="button"
                        data-testid="journey-dossier-goto-button"
                        onClick={onGoToDocuments}
                        className="mt-3 rounded-full px-4 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90"
                        style={{ backgroundColor: PETROLE }}
                      >
                        Compléter mon dossier
                      </button>
                    )}
                  </div>
                )}

                {step.key === "souscription" && step.state === "action_requise" && (
                  <p className="mt-2 text-xs leading-relaxed text-amber-700">Choisissez votre secteur ci-dessous pour finaliser votre souscription.</p>
                )}
                {step.key === "activation" && step.state === "en_cours" && (
                  <p className="mt-2 text-xs leading-relaxed text-sky-700">CozyHome vérifie votre dossier avant d'activer votre territoire.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TERRITORY_STATUS_LABELS = {
  disponible: "Disponible", reserve: "Réservé — en attente d'activation", attribue: "Actif", suspendu: "Suspendu",
};
const TERRITORY_STATUS_CLS = {
  disponible: "bg-sky-50 text-sky-700", reserve: "bg-amber-50 text-amber-700",
  attribue: "bg-sage/15 text-navy", suspendu: "bg-red-50 text-red-600",
};

export const PartnerTerritoryBanner = ({ territory }) => {
  if (!territory) return null;
  const chosen = territory.chosen_territories || [];
  const proposed = territory.proposed_territory;
  if (chosen.length === 0 && !proposed) return null;

  return (
    <div data-testid="journey-territory-banner" className="mt-6 rounded-3xl p-5 sm:p-6" style={{ backgroundColor: `${PETROLE}0D` }}>
      <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: PETROLE }}>Votre secteur CozyHome</p>
      <div className="mt-3 space-y-2">
        {chosen.length > 0
          ? chosen.map((t) => (
              <div key={t.id} data-testid={`journey-territory-${t.code}`} className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-bold text-navy">
                  <MapPin className="h-4 w-4 shrink-0" style={{ color: PETROLE }} /> {t.name}
                </span>
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${TERRITORY_STATUS_CLS[t.status]}`}>
                  {TERRITORY_STATUS_LABELS[t.status] || t.status}
                </span>
              </div>
            ))
          : (
            <div data-testid={`journey-territory-${proposed.code}`} className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-sm font-bold text-navy">
                <MapPin className="h-4 w-4 shrink-0" style={{ color: PETROLE }} /> {proposed.name}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-500">Proposé — à confirmer</span>
            </div>
          )}
      </div>
    </div>
  );
};
